/**
 * Conditional logging utility that respects the CONSOLE_LOGGING_ENABLED feature flag
 */

import { FeatureFlagName } from '@curbside-quest/shared';

// Logger initialization states
enum LoggerState {
  NOT_INITIALIZED = 'not_initialized',
  INITIALIZING = 'initializing',
  INITIALIZED = 'initialized',
  FAILED = 'failed'
}

// Global state to track the feature flag value
let consoleLoggingEnabled: boolean | null = null;
let featureFlagService: any = null;
let loggerState: LoggerState = LoggerState.NOT_INITIALIZED;

/**
 * Initialize the logger with the feature flag service
 * This should be called once the feature flag system is available
 */
export function initializeLogger(getFeatureFlagValue: (flag: FeatureFlagName) => boolean) {
  // Prevent multiple initializations
  if (loggerState === LoggerState.INITIALIZED || loggerState === LoggerState.INITIALIZING) {
    return;
  }
  
  loggerState = LoggerState.INITIALIZING;
  featureFlagService = getFeatureFlagValue;
  

  
  // In local development, environment variables take precedence
  if (isLocalDevelopment()) {
    const envOverride = import.meta.env.VITE_FEATURE_CONSOLE_LOGGING_ENABLED;
    if (envOverride !== undefined) {
      consoleLoggingEnabled = envOverride.toLowerCase() === 'true';
      loggerState = LoggerState.INITIALIZED;
      return;
    }
  }
  
  // In staging/production, Remote Config takes priority
  try {
    consoleLoggingEnabled = getFeatureFlagValue('CONSOLE_LOGGING_ENABLED');
    loggerState = LoggerState.INITIALIZED;
  } catch (error) {
    consoleLoggingEnabled = getEnvironmentBasedDefault();
    loggerState = LoggerState.FAILED;
  }
}

/**
 * Update the cached feature flag value
 * This can be called when feature flags are refreshed
 */
export function updateLoggingState() {
  // In local development, environment variables take precedence
  if (isLocalDevelopment()) {
    const envOverride = import.meta.env.VITE_FEATURE_CONSOLE_LOGGING_ENABLED;
    if (envOverride !== undefined) {
      consoleLoggingEnabled = envOverride.toLowerCase() === 'true';
      return;
    }
  }
  
  // In staging/production, update from Remote Config
  if (featureFlagService) {
    try {
      consoleLoggingEnabled = featureFlagService('CONSOLE_LOGGING_ENABLED');
      loggerState = LoggerState.INITIALIZED;
    } catch (error) {
      consoleLoggingEnabled = getEnvironmentBasedDefault();
      loggerState = LoggerState.FAILED;
    }
  }
}

/**
 * Environment detection functions
 */
function isLocalDevelopment(): boolean {
  // Local development: DEV mode with demo project or no project ID
  return import.meta.env.DEV && (
    !import.meta.env.VITE_FIREBASE_PROJECT_ID || 
    import.meta.env.VITE_FIREBASE_PROJECT_ID.startsWith('demo-')
  );
}

function isStagingEnvironment(): boolean {
  return import.meta.env.VITE_NODE_ENV === 'staging' || 
         import.meta.env.MODE === 'staging';
}

function isProductionEnvironment(): boolean {
  return import.meta.env.VITE_NODE_ENV === 'production' || 
         import.meta.env.MODE === 'production' ||
         (!isLocalDevelopment() && !isStagingEnvironment());
}

// We need to avoid circular dependencies, so we'll check the feature flag directly
// This is a simplified check that works without importing the full feature flag system
function isConsoleLoggingEnabled(): boolean {
  // Priority 1: In local development, environment variables take precedence
  if (isLocalDevelopment()) {
    const envOverride = import.meta.env.VITE_FEATURE_CONSOLE_LOGGING_ENABLED;
    if (envOverride !== undefined) {
      return envOverride.toLowerCase() === 'true';
    }
    // Local dev default: enabled
    return true;
  }
  
  // Priority 2: In staging/production, use Remote Config if available
  if (loggerState === LoggerState.INITIALIZED && consoleLoggingEnabled !== null) {
    return consoleLoggingEnabled;
  }
  
  // Priority 3: Emergency environment variable override (staging/production)
  const envOverride = import.meta.env.VITE_FEATURE_CONSOLE_LOGGING_ENABLED;
  if (envOverride !== undefined) {
    return envOverride.toLowerCase() === 'true';
  }
  
  // Priority 4: Fallback defaults
  if (loggerState === LoggerState.NOT_INITIALIZED || loggerState === LoggerState.INITIALIZING) {
    // Still initializing - use conservative default
    return isLocalDevelopment();
  }
  
  // Failed to initialize or no value - use environment-based default
  return getEnvironmentBasedDefault();
}

function getEnvironmentBasedDefault(): boolean {
  // Local development: default to enabled
  if (isLocalDevelopment()) {
    return true;
  }
  
  // Staging/Production: default to disabled
  return false;
}

/**
 * Conditional console.log that respects CONSOLE_LOGGING_ENABLED feature flag
 */
export function conditionalLog(...args: any[]): void {
  const enabled = isConsoleLoggingEnabled();
  
  if (enabled) {
    console.log(...args);
  }
}

/**
 * Conditional console.warn that respects CONSOLE_LOGGING_ENABLED feature flag
 */
export function conditionalWarn(...args: any[]): void {
  if (isConsoleLoggingEnabled()) {
    console.warn(...args);
  }
}

/**
 * Conditional console.error that respects CONSOLE_LOGGING_ENABLED feature flag
 * Note: Errors are always logged regardless of the flag for debugging purposes
 */
export function conditionalError(...args: any[]): void {
  console.error(...args);
}

/**
 * Conditional console.info that respects CONSOLE_LOGGING_ENABLED feature flag
 */
export function conditionalInfo(...args: any[]): void {
  if (isConsoleLoggingEnabled()) {
    console.info(...args);
  }
}

/**
 * Check if console logging is currently enabled
 */
export function isLoggingEnabled(): boolean {
  return isConsoleLoggingEnabled();
}

/**
 * Debug function to show current logging state (always logs)
 */
export function debugLoggingState(): void {
  const envVar = import.meta.env.VITE_FEATURE_CONSOLE_LOGGING_ENABLED;
  const enabled = isConsoleLoggingEnabled();
  
  console.log('🔍 Logger Debug State:', {
    // Environment Detection
    isDev: import.meta.env.DEV,
    mode: import.meta.env.MODE,
    nodeEnv: import.meta.env.VITE_NODE_ENV,
    isLocalDev: isLocalDevelopment(),
    isStaging: isStagingEnvironment(),
    isProduction: isProductionEnvironment(),
    
    // State
    loggerState,
    envVar,
    cached: consoleLoggingEnabled,
    enabled,
    hasFeatureFlagService: !!featureFlagService,
    
    // Firebase Project
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID
  });
}